import React, { useState } from 'react';
import { Smartphone, Tablet, Monitor, Building2, MapPin, ShieldCheck, Mail, Phone, ExternalLink, Star, Image as ImageIcon } from 'lucide-react';
interface EditorCard {
  id: string;
  title: string;
  url: string;
  description: string;
  image: string;
  enabled: boolean;
  price?: string;
  status?: string;
}
interface EditorSection {
  id: string;
  type: 'listings-videos' | 'custom-links';
  title: string;
  description: string;
  layout: string;
  cards: EditorCard[];
  collapsed: boolean;
}
interface DevicePreviewProps {
  profileImage?: string;
  displayName?: string;
  title?: string;
  brokerage?: string;
  location?: string;
  dreNumber?: string;
  email?: string;
  phone?: string;
  address?: string;
  websites?: Array<{
    id: number;
    title: string;
    url: string;
  }>;
  reviews?: Array<{
    id: number;
    platform: string;
    rating: string;
    count: string;
    url: string;
    enabled: boolean;
  }>;
  aiSummaryEnabled?: boolean;
  sections: EditorSection[];
  socialLinks: Array<{
    enabled: boolean;
    icon: any;
    url?: string;
  }>;
}
type DeviceType = 'mobile' | 'tablet' | 'desktop';
export function DevicePreview({
  profileImage = 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=400&h=400&fit=crop',
  displayName = 'Marcus Chen',
  title = 'Senior real estate advisor',
  brokerage = 'Compass Real Estate · The Chen Group',
  location = 'San Francisco Bay Area',
  dreNumber = '02145678',
  email = 'marcus@chengroup.com',
  phone = '(415) 555-0198',
  address = '1200 Market St, San Francisco, CA 94102',
  websites = [],
  reviews = [],
  aiSummaryEnabled = true,
  sections,
  socialLinks
}: DevicePreviewProps) {
  const [device, setDevice] = useState<DeviceType>('mobile');
  const renderSectionPreview = (section: EditorSection) => {
    const enabledCards = section.cards.filter(c => c.enabled);
    if (enabledCards.length === 0) return null;
    if (section.type === 'listings-videos') {
      if (section.layout === 'carousel') {
        return <div className="flex gap-2 overflow-x-auto hide-scrollbar pb-2" data-id="element-93">
            {enabledCards.map(card => <div key={card.id} className="shrink-0 w-32 bg-white rounded-lg border border-brand-light p-2" data-id="element-94">
                {card.image && <div className="w-full h-20 rounded overflow-hidden mb-1 bg-brand-light relative" data-id="element-95">
                    <img src={card.image} alt={card.title} className="w-full h-full object-cover" data-id="element-96" />
                    {card.status && <span className="absolute top-1 left-1 text-[8px] font-medium px-1.5 py-0.5 rounded-full border bg-white/90 text-brand-dark border-brand-light backdrop-blur-sm" data-id="element-97">
                        {card.status}
                      </span>}
                  </div>}
                <p className="text-[10px] font-medium text-brand-dark truncate" data-id="element-98">
                  {card.title}
                </p>
                {card.price && <p className="text-[9px] font-mono text-brand-muted" data-id="element-99">
                    {card.price}
                  </p>}
              </div>)}
          </div>;
      }
      if (section.layout === 'small-card') {
        return <div className="grid grid-cols-2 gap-2" data-id="element-100">
            {enabledCards.map(card => <div key={card.id} className="bg-white rounded-lg border border-brand-light p-2" data-id="element-101">
                {card.image && <div className="w-full h-16 rounded overflow-hidden mb-1 bg-brand-light relative" data-id="element-102">
                    <img src={card.image} alt={card.title} className="w-full h-full object-cover" data-id="element-103" />
                    {card.status && <span className="absolute top-1 left-1 text-[8px] font-medium px-1.5 py-0.5 rounded-full border bg-white/90 text-brand-dark border-brand-light backdrop-blur-sm" data-id="element-104">
                        {card.status}
                      </span>}
                  </div>}
                <p className="text-[10px] font-medium text-brand-dark truncate" data-id="element-105">
                  {card.title}
                </p>
                {card.price && <p className="text-[9px] font-mono text-brand-muted" data-id="element-106">
                    {card.price}
                  </p>}
              </div>)}
          </div>;
      }
      return <div className="space-y-2" data-id="element-107">
          {enabledCards.map(card => <div key={card.id} className="bg-white rounded-lg border border-brand-light p-3 flex gap-2" data-id="element-108">
              {card.image && <div className="w-16 h-16 rounded overflow-hidden shrink-0 bg-brand-light relative" data-id="element-109">
                  <img src={card.image} alt={card.title} className="w-full h-full object-cover" data-id="element-110" />
                </div>}
              <div className="flex-1 min-w-0" data-id="element-111">
                <div className="flex items-center justify-between gap-2" data-id="element-112">
                  <p className="text-xs font-medium text-brand-dark truncate" data-id="element-113">
                    {card.title}
                  </p>
                  {card.status && <span className="text-[8px] font-medium px-1.5 py-0.5 rounded-full border bg-emerald-50 text-emerald-700 border-emerald-200 shrink-0" data-id="element-114">
                      {card.status}
                    </span>}
                </div>
                <p className="text-[10px] text-brand-muted truncate" data-id="element-115">
                  {card.description}
                </p>
                {card.price && <p className="text-[10px] font-mono text-brand-dark mt-1" data-id="element-116">
                    {card.price}
                  </p>}
              </div>
            </div>)}
        </div>;
    }
    if (section.layout === 'grid') {
      return <div className="grid grid-cols-2 gap-2" data-id="element-117">
          {enabledCards.map(card => <div key={card.id} className="py-2 px-3 bg-brand-offwhite border border-brand-light rounded-lg text-center text-[10px] font-medium text-brand-dark" data-id="element-118">
              {card.title}
            </div>)}
        </div>;
    }
    return <div className="space-y-2" data-id="element-119">
        {enabledCards.map(card => <div key={card.id} className="w-full py-2 px-3 bg-brand-offwhite border border-brand-light rounded-lg text-center text-[10px] font-medium text-brand-dark" data-id="element-120">
            {card.title}
          </div>)}
      </div>;
  };
  const getFrameStyles = () => {
    if (device === 'mobile') {
      return 'w-[320px] h-[650px] border-[10px] border-gray-900 rounded-[2.5rem]';
    }
    if (device === 'tablet') {
      return 'w-[768px] h-[600px] border-4 border-gray-800 rounded-2xl';
    }
    return 'w-full h-[600px] border-2 border-gray-300 rounded-xl';
  };
  const isMobile = device === 'mobile';
  return <div className="hidden lg:flex w-[900px] bg-white border-l border-brand-light flex-col items-center py-8 sticky top-0 h-screen overflow-hidden" data-id="element-121">
      <div className="mb-4 flex items-center gap-2 bg-brand-offwhite rounded-full p-1 border border-brand-light" data-id="element-122">
        <button onClick={() => setDevice('mobile')} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${device === 'mobile' ? 'bg-brand-forest text-white' : 'text-brand-muted hover:text-brand-dark'}`} data-id="element-123">
          <Smartphone size={14} data-id="element-124" />
          Mobile
        </button>
        <button onClick={() => setDevice('tablet')} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${device === 'tablet' ? 'bg-brand-forest text-white' : 'text-brand-muted hover:text-brand-dark'}`} data-id="element-125">
          <Tablet size={14} data-id="element-126" />
          Tablet
        </button>
        <button onClick={() => setDevice('desktop')} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${device === 'desktop' ? 'bg-brand-forest text-white' : 'text-brand-muted hover:text-brand-dark'}`} data-id="element-127">
          <Monitor size={14} data-id="element-128" />
          Desktop
        </button>
      </div>

      <div className="mb-4 px-4 py-2 bg-brand-offwhite rounded-full border border-brand-light text-xs font-mono text-brand-muted flex items-center gap-2" data-id="element-129">
        <span className="w-2 h-2 rounded-full bg-green-500" data-id="element-130"></span>
        eaves.pro/marcuschen
      </div>

      <div className={`${getFrameStyles()} overflow-hidden shadow-2xl relative bg-brand-offwhite transition-all duration-300`} data-id="element-131">
        {device === 'mobile' && <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-6 bg-gray-900 rounded-b-2xl z-50" data-id="element-132"></div>}

        {device === 'desktop' && <div className="absolute top-0 left-0 right-0 h-8 bg-gray-100 border-b border-gray-300 flex items-center px-3 gap-1.5 z-50" data-id="element-133">
            <div className="w-2 h-2 rounded-full bg-red-400" data-id="element-134"></div>
            <div className="w-2 h-2 rounded-full bg-yellow-400" data-id="element-135"></div>
            <div className="w-2 h-2 rounded-full bg-green-400" data-id="element-136"></div>
            <div className="ml-2 flex-1 bg-white rounded px-2 py-0.5 text-[9px] text-brand-muted font-mono" data-id="element-137">
              eaves.pro/marcuschen
            </div>
          </div>}

        <div className={`h-full overflow-y-auto hide-scrollbar ${device === 'desktop' ? 'pt-8' : ''}`} data-id="element-138">
          <div className={`${isMobile ? 'h-24' : 'h-32 md:h-48'} bg-gradient-to-r from-brand-sage via-brand-sandstoneBg to-brand-light relative`} data-id="element-139">
            <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-brand-forest to-transparent" data-id="element-140"></div>
          </div>

          <div className={`${isMobile ? 'px-4 -mt-12' : 'px-6 md:px-12 -mt-16 md:-mt-24 max-w-5xl mx-auto'} relative z-10`} data-id="element-141">
            <div className={`bg-white ${isMobile ? 'rounded-xl p-4' : 'rounded-2xl p-6 md:p-8'} shadow-sm border border-brand-light`} data-id="element-142">
              <div className={`flex ${isMobile ? 'flex-col' : 'flex-col md:flex-row'} gap-4 md:gap-8`} data-id="element-143">
                <div className={`flex flex-col ${isMobile ? 'items-center' : 'items-center md:items-start'} shrink-0`} data-id="element-144">
                  <div className={`${isMobile ? 'w-20 h-20 -mt-10 mb-3' : 'w-24 h-24 md:w-32 md:h-32 -mt-12 md:-mt-16'} rounded-full border-4 border-white shadow-sm bg-brand-offwhite mx-auto overflow-hidden flex items-center justify-center relative z-10`} data-id="element-145">
                    {profileImage ? <img src={profileImage} alt={displayName} className="w-full h-full object-cover" data-id="element-146" /> : <ImageIcon size={isMobile ? 24 : 32} className="text-brand-light" data-id="element-147" />}
                  </div>
                </div>

                <div className={`flex-1 ${isMobile ? 'text-center' : 'text-center md:text-left'} pt-2 md:pt-0`} data-id="element-148">
                  <div className={`flex flex-col ${isMobile ? '' : 'md:flex-row md:items-start'} justify-between gap-4 mb-4`} data-id="element-149">
                    <div data-id="element-150">
                      <h1 className={`${isMobile ? 'text-base' : 'text-2xl md:text-3xl'} font-serif font-semibold text-brand-dark mb-0.5`} data-id="element-151">
                        {displayName}
                      </h1>
                      <h2 className={`${isMobile ? 'text-[10px]' : 'text-sm md:text-base'} text-brand-muted font-serif italic mb-3`} data-id="element-152">
                        {title}
                      </h2>

                      {brokerage && <div className={`inline-flex items-center justify-center gap-1 bg-brand-sage/50 border border-brand-sage text-brand-forest ${isMobile ? 'px-2 py-0.5 text-[9px]' : 'px-3 py-1 text-xs'} rounded-full font-medium tracking-wide mb-3`} data-id="element-153">
                          <Building2 size={isMobile ? 10 : 14} data-id="element-154" />
                          <span data-id="element-155">{brokerage}</span>
                        </div>}

                      <div className={`flex flex-wrap items-center justify-center ${isMobile ? '' : 'md:justify-start'} gap-2 ${isMobile ? 'text-[9px]' : 'text-xs md:text-sm'} text-brand-muted mb-4`} data-id="element-156">
                        {location && <span className="flex items-center gap-1" data-id="element-157">
                            <MapPin size={isMobile ? 10 : 14} className="text-brand-forest" data-id="element-158" />
                            {location}
                          </span>}
                        {location && dreNumber && <span className="text-brand-light" data-id="element-159">|</span>}
                        {dreNumber && <span className="flex items-center gap-1" data-id="element-160">
                            <ShieldCheck size={isMobile ? 10 : 14} className="text-brand-forest" data-id="element-161" />
                            DRE #{dreNumber}
                          </span>}
                      </div>
                    </div>

                    <div className={`flex flex-wrap justify-center ${isMobile ? '' : 'md:justify-end'} gap-1.5 mb-4`} data-id="element-162">
                      {socialLinks.filter(l => l.enabled).map((link, i) => {
                      const Icon = link.icon;
                      return <a key={i} href={link.url || '#'} className={`${isMobile ? 'w-7 h-7' : 'w-8 h-8 md:w-9 md:h-9'} rounded-full bg-brand-offwhite hover:bg-brand-sage flex items-center justify-center transition-colors`} data-id="element-163">
                              <Icon size={isMobile ? 12 : 16} className="text-brand-muted" data-id="element-164" />
                            </a>;
                    })}

                      {email && <a href={`mailto:${email}`} className={`flex items-center justify-center rounded-full bg-brand-offwhite hover:bg-brand-sage transition-colors ${isMobile ? 'w-7 h-7' : 'h-8 md:h-9 px-2.5 gap-1.5'}`} data-id="element-165">
                          <Mail size={isMobile ? 12 : 16} className="text-brand-muted" data-id="element-166" />
                          {!isMobile && <span className="text-[10px] md:text-xs text-brand-dark font-medium" data-id="element-167">
                              {email}
                            </span>}
                        </a>}

                      {phone && <a href={`tel:${phone}`} className={`flex items-center justify-center rounded-full bg-brand-offwhite hover:bg-brand-sage transition-colors ${isMobile ? 'w-7 h-7' : 'h-8 md:h-9 px-2.5 gap-1.5'}`} data-id="element-168">
                          <Phone size={isMobile ? 12 : 16} className="text-brand-muted" data-id="element-169" />
                          {!isMobile && <span className="text-[10px] md:text-xs text-brand-dark font-medium" data-id="element-170">
                              {phone}
                            </span>}
                        </a>}
                    </div>
                  </div>

                  {websites && websites.length > 0 && <div className={`flex flex-wrap items-center justify-center ${isMobile ? '' : 'md:justify-start'} gap-2 mb-6`} data-id="element-171">
                      {websites.map(website => <a key={website.id} href={website.url || '#'} className={`inline-flex items-center gap-1.5 ${isMobile ? 'px-2 py-1 text-[10px]' : 'px-3 py-1.5 text-xs md:text-sm'} rounded-md bg-brand-offwhite hover:bg-brand-sage text-brand-dark transition-colors border border-brand-light`} data-id="element-172">
                          <span className="font-medium" data-id="element-173">
                            {website.title || 'Website'}
                          </span>
                          <ExternalLink size={isMobile ? 10 : 12} className="text-brand-muted opacity-50" data-id="element-174" />
                        </a>)}
                    </div>}

                  {!isMobile && <div className="grid grid-cols-2 gap-4 pt-6 border-t border-brand-light" data-id="element-175">
                      <div className="text-center md:text-left" data-id="element-176">
                        <p className="text-lg md:text-xl font-mono text-brand-dark font-semibold mb-1" data-id="element-177">
                          14 years
                        </p>
                        <p className="text-[10px] md:text-xs text-brand-muted" data-id="element-178">
                          Experience
                        </p>
                      </div>
                      <div className="text-center md:text-left" data-id="element-179">
                        <p className="text-lg md:text-xl font-mono text-brand-dark font-semibold mb-1" data-id="element-180">
                          Top 13%
                        </p>
                        <p className="text-[10px] md:text-xs text-brand-muted" data-id="element-181">
                          In 94109
                        </p>
                      </div>
                    </div>}
                </div>
              </div>
            </div>

            {reviews && reviews.some(r => r.enabled) && <div className={`mt-4 bg-white ${isMobile ? 'rounded-xl p-3' : 'rounded-2xl p-6'} border border-brand-light`} data-id="element-182">
                <h3 className={`${isMobile ? 'text-xs' : 'text-sm md:text-base'} font-serif font-medium text-brand-dark mb-2`} data-id="element-183">
                  Reviews
                </h3>
                <div className="flex flex-wrap gap-2 mb-2" data-id="element-184">
                  {reviews.filter(r => r.enabled).map(review => <div key={review.id} className={`inline-flex items-center gap-1 px-2 py-1 rounded-full bg-brand-offwhite border border-brand-light ${isMobile ? 'text-[9px]' : 'text-xs'}`} data-id="element-185">
                        <Star size={isMobile ? 10 : 12} className="fill-amber-400 text-amber-400" data-id="element-186" />
                        <span className="font-mono font-medium text-brand-dark" data-id="element-187">
                          {review.rating}
                        </span>
                        <span className="text-brand-dark" data-id="element-188">
                          {review.platform}
                        </span>
                        <span className="text-brand-muted font-mono" data-id="element-189">
                          ({review.count})
                        </span>
                      </div>)}
                </div>
                {aiSummaryEnabled && <p className={`${isMobile ? 'text-[9px]' : 'text-xs md:text-sm'} text-brand-muted italic leading-relaxed`} data-id="element-190">
                    "Across reviews on Yelp, Zillow, Google, and Facebook,
                    clients consistently praise Marcus and his team for their
                    deep neighborhood expertise, guiding first-time and repeat
                    sellers through every detail, and negotiating well above
                    asking price with genuine professionalism."
                  </p>}
              </div>}

            <div className="mt-4 space-y-4 pb-8" data-id="element-191">
              {sections.map(section => <div key={section.id} className={`bg-white ${isMobile ? 'rounded-xl p-3' : 'rounded-2xl p-6'} border border-brand-light`} data-id="element-192">
                  <h3 className={`${isMobile ? 'text-xs' : 'text-sm md:text-base'} font-serif font-medium text-brand-dark mb-1`} data-id="element-193">
                    {section.title}
                  </h3>
                  {section.description && <p className={`${isMobile ? 'text-[9px]' : 'text-xs md:text-sm'} text-brand-muted mb-3`} data-id="element-194">
                      {section.description}
                    </p>}
                  {renderSectionPreview(section)}
                </div>)}
            </div>
          </div>
        </div>
      </div>
    </div>;
}